import { useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { InvestmentEntityLookupDocument } from "#graphql/supergraphHooks";
import { toDbId, toGlobalId } from "#utils/globalId";
import type {
  InvestmentEntityLookupQuery,
  InvestmentEntityLookupQueryVariables,
} from "#graphql/supergraphTypes";

// Resolves display names for a specific set of numeric account ids. The
// classification read model persists only entityId — fay returns
// entityName: null — so the confirm screen must map id -> name itself. The
// assign/move picker only returns entities matching the current search text, so
// an auto-resolved entity is usually absent from it. This fetches names for
// exactly the ids present in the ingestion, batched in one request. Uses the
// same `InvestmentEntityLookup` operation as the picker, encoding each numeric
// id to a relay global id and decoding the returned node ids back.

export type UseEntityNamesReturn = {
  names: Map<string, string>;
  loading: boolean;
};

type EntityNamesState = {
  arenaId: number | undefined;
  names: Map<string, string>;
};

const EMPTY_NAMES = new Map<string, string>();

export function useEntityNames(entityIds: string[]): UseEntityNamesReturn {
  return useEntityNamesFromGraph(entityIds, undefined, false);
}

// Package-internal: arena-scoped staff lookup for ClassificationConfirm only.
export function useStaffEntityNames(
  entityIds: string[],
  arenaId: number | undefined,
): UseEntityNamesReturn {
  return useEntityNamesFromGraph(entityIds, arenaId, true);
}

function useEntityNamesFromGraph(
  entityIds: string[],
  arenaId: number | undefined,
  staffScoped: boolean,
): UseEntityNamesReturn {
  const client = useApolloClient();
  const [namesState, setNamesState] = useState<EntityNamesState>({
    arenaId,
    names: new Map(),
  });
  const [loading, setLoading] = useState(false);
  // Ids already requested (resolved or in-flight), so a re-render with the same
  // set doesn't refetch and we never loop on the names state.
  const requestedRef = useRef<Set<string>>(new Set());
  const requestedArenaRef = useRef<number>();

  // Order-independent, stable dependency for the unique id set.
  const key = useMemo(
    () =>
      Array.from(new Set(entityIds.filter(Boolean)))
        .sort()
        .join(","),
    [entityIds],
  );

  useEffect(() => {
    if (staffScoped && arenaId === undefined) {
      return undefined;
    }

    // The ref's Set identity is stable across renders; capture it so the cleanup
    // closes over the same instance the effect mutated (react-hooks/exhaustive-deps).
    const requested = requestedRef.current;
    if (requestedArenaRef.current !== arenaId) {
      requested.clear();
      requestedArenaRef.current = arenaId;
      setNamesState({ arenaId, names: new Map() });
      setLoading(false);
    }
    const missing = key.split(",").filter((id) => id && !requested.has(id));
    if (missing.length === 0) {
      return undefined;
    }
    for (const id of missing) {
      requested.add(id);
    }

    let cancelled = false;
    // Whether the query has resolved/rejected. On a mid-flight cancel (settled
    // still false) the cleanup drops these ids from requestedRef so a later
    // render retries — otherwise an id cancelled in-flight stays marked
    // "requested" forever and the raw id leaks, the exact failure this prevents.
    let settled = false;
    setLoading(true);
    client
      .query<InvestmentEntityLookupQuery, InvestmentEntityLookupQueryVariables>({
        query: InvestmentEntityLookupDocument,
        ...(staffScoped ? { context: { clientName: "staff", arenaId } } : {}),
        variables: {
          ids: missing.map((id) => toGlobalId("AccountObjectType", id)),
        },
        fetchPolicy: staffScoped ? "network-only" : "cache-first",
      })
      .then((res) => {
        settled = true;
        if (cancelled) {
          return;
        }
        const resolved = (res.data?.investmentEntities?.edges ?? [])
          .map((edge) => [String(toDbId(edge.node.id)), edge.node.name] as const)
          .filter(([, name]) => Boolean(name));
        if (resolved.length) {
          setNamesState((prev) => {
            const next = new Map(prev.arenaId === arenaId ? prev.names : []);
            for (const [id, name] of resolved) {
              next.set(id, name);
            }
            return { arenaId, names: next };
          });
        }
        setLoading(false);
      })
      .catch(() => {
        settled = true;
        // Drop these ids from the requested set so a later render retries; a
        // transient failure otherwise permanently blocks resolution and leaks
        // the raw id. (Genuinely missing/unauthorized ids fall back to the id
        // at the display layer, and simply re-resolve to nothing on retry.)
        if (!cancelled) {
          for (const id of missing) {
            requested.delete(id);
          }
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      // Cancelled before the query settled: un-mark these ids so the next render
      // re-resolves them instead of leaving them stuck "requested".
      if (!settled) {
        for (const id of missing) {
          requested.delete(id);
        }
      }
    };
  }, [key, client, arenaId, staffScoped]);

  const names = namesState.arenaId === arenaId ? namesState.names : EMPTY_NAMES;
  return { names, loading };
}
